import type { CellRange } from './range.js';
import type { Accessor, RowGroup, SortSpec } from './rows.js';
import { filterRows, groupRows, sortRows } from './rows.js';

/** Session-only view state of a table (decision Q14: never persisted). */
export interface GridViewState {
  sort: SortSpec[];
  filters: Record<string, string>;
  groupBy: string | null;
  selection: CellRange | null;
}

/** Fresh view state: no sort, filter, grouping or selection. */
export function initialGridViewState(): GridViewState {
  return { sort: [], filters: {}, groupBy: null, selection: null };
}

/**
 * Header click: none → asc → desc → none for `key`. With `additive` (Shift) other sort keys stay,
 * otherwise they are replaced.
 */
export function toggleSort(state: GridViewState, key: string, additive = false): GridViewState {
  const current = state.sort.find((s) => s.key === key);
  const others = additive ? state.sort.filter((s) => s.key !== key) : [];
  const next: SortSpec[] =
    current === undefined
      ? [{ key, dir: 'asc' }]
      : current.dir === 'asc'
        ? [{ key, dir: 'desc' }]
        : [];
  return { ...state, sort: [...others, ...next] };
}

/** Filters, sorts and groups rows (a single group with key '' when not grouped). */
export function applyView<R>(
  rows: readonly R[],
  state: GridViewState,
  get?: Accessor<R>,
): RowGroup<R>[] {
  const visible = sortRows(filterRows(rows, state.filters, get), state.sort, get);
  return state.groupBy === null
    ? [{ key: '', rows: visible }]
    : groupRows(visible, state.groupBy, get);
}
